import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  limit,
  addDoc,
  updateDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  BusinessAccount, 
  BusinessListItem, 
  ServiceCategory, 
  Review, 
  Booking,
  PortfolioImage,
  StaffMember
} from '../types';

// Business Accounts
export const getBusinesses = async (): Promise<BusinessListItem[]> => {
  try {
    const businessesRef = collection(db, 'BusinessAccounts');
    const snapshot = await getDocs(businessesRef);
    
    return snapshot.docs.map(doc => {
      const data = doc.data() as BusinessAccount;
      return {
        businessId: doc.id,
        name: data.Name,
        postCode: data.Postcode,
        address: data.Address,
        businessImage: data.BusinessImageUrl || '',
        businessOperatingHours: data.BusinessOperatingHours,
        rating: data.Rating || 4.5,
        reviewCount: data.ReviewCount || 0,
      };
    });
  } catch (error) {
    console.error('Error fetching businesses:', error);
    return [];
  }
};

export const getBusinessById = async (businessId: string): Promise<BusinessAccount | null> => {
  try {
    const businessRef = doc(db, 'BusinessAccounts', businessId);
    const businessSnap = await getDoc(businessRef);
    
    if (businessSnap.exists()) {
      return {
        id: businessSnap.id,
        ...businessSnap.data()
      } as BusinessAccount;
    }
    return null;
  } catch (error) {
    console.error('Error fetching business:', error);
    return null;
  }
};

// Services
export const getBusinessServices = async (businessId: string): Promise<ServiceCategory[]> => {
  try {
    const servicesRef = collection(db, 'BusinessAccounts', businessId, 'Services');
    const snapshot = await getDocs(servicesRef);
    
    return snapshot.docs.map(doc => ({
      userType: doc.id,
      data: doc.data()
    })) as ServiceCategory[];
  } catch (error) {
    console.error('Error fetching services:', error);
    return [];
  }
};

// Staff
export const getBusinessStaff = async (businessId: string): Promise<StaffMember[]> => {
  try {
    const staffRef = collection(db, 'BusinessAccounts', businessId, 'Staff');
    const snapshot = await getDocs(staffRef);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as StaffMember[];
  } catch (error) {
    console.error('Error fetching staff:', error);
    return [];
  }
};

// Reviews
export const getBusinessReviews = async (businessId: string): Promise<Review[]> => {
  try {
    const reviewsRef = collection(db, 'BusinessAccounts', businessId, 'Reviews');
    const q = query(reviewsRef, orderBy('date', 'desc'), limit(50));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        date: data.date?.toDate?.() || data.date
      };
    }) as Review[];
  } catch (error) {
    console.error('Error fetching reviews:', error);
    return [];
  }
};

// Portfolio
export const getBusinessPortfolio = async (businessId: string): Promise<PortfolioImage[]> => {
  try {
    const portfolioRef = collection(db, 'BusinessAccounts', businessId, 'Portfolio');
    const q = query(portfolioRef, orderBy('createdAt', 'desc'), limit(50));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as PortfolioImage[];
  } catch (error) {
    console.error('Error fetching portfolio:', error);
    return [];
  }
};

// Bookings
export const createBooking = async (booking: Omit<Booking, 'id'>): Promise<string | null> => {
  try {
    const bookingsRef = collection(db, 'Bookings');
    const docRef = await addDoc(bookingsRef, {
      ...booking,
      createdAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating booking:', error);
    return null;
  }
};

export const getCustomerBookings = async (customerId: string): Promise<Booking[]> => {
  try {
    const bookingsRef = collection(db, 'Bookings');
    const q = query(
      bookingsRef, 
      where('customerId', '==', customerId),
      orderBy('date', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Booking[];
  } catch (error) {
    console.error('Error fetching bookings:', error);
    return [];
  }
};

export const updateBookingStatus = async (
  bookingId: string, 
  status: Booking['status']
): Promise<boolean> => {
  try {
    const bookingRef = doc(db, 'Bookings', bookingId);
    await updateDoc(bookingRef, { status });
    return true;
  } catch (error) {
    console.error('Error updating booking:', error);
    return false;
  }
};

// Search
export const searchBusinesses = async (searchTerm: string): Promise<BusinessListItem[]> => {
  try {
    // For a real implementation, you'd use Algolia or similar
    // For now, we'll fetch all and filter client-side
    const businesses = await getBusinesses();
    const term = searchTerm.toLowerCase();
    
    return businesses.filter(b => 
      b.name.toLowerCase().includes(term) ||
      b.address.toLowerCase().includes(term) ||
      b.postCode.toLowerCase().includes(term)
    );
  } catch (error) {
    console.error('Error searching businesses:', error);
    return [];
  }
};

